import React from "react";
import { LAYOUTS, COLORS } from "./staticTemplate/tokens";
import { buildData } from "./staticTemplate/shared";
import { NuovoAnnuncio, PrezzoAggiornato, Venduto, OpenHouse } from "./staticTemplate/SocialPosts";
import { Brochure, BrochureSocial } from "./staticTemplate/Brochure";
import { VolantinoStima } from "./staticTemplate/VolantinoStima";
import { StoryStima, StoryNuovo, StoryVenduto } from "./staticTemplate/Stories";

/**
 * StaticTemplatePreview — dispatcher dei template statici del Social Studio
 * (Social Posts · Stories · Brochure A4 · Volantino A5).
 *
 * Architettura (post refactor iter 261):
 *   • tokens.js          → LAYOUTS, COLORS (4 layout × 8 colori = 32 combos)
 *   • shared.jsx         → Watermark, Logo, buildData()
 *   • SocialPosts.jsx    → NuovoAnnuncio, PrezzoAggiornato, Venduto, OpenHouse
 *   • Brochure.jsx       → Brochure (4 pagine A4) + cover variants
 *   • VolantinoStima.jsx → Volantino postale A5 (fronte/retro)
 *   • Stories.jsx        → Instagram stories (Stima, Nuovo, Venduto)
 *
 * Props:
 *   template:  "nuovo_annuncio" | "prezzo_aggiornato" | "venduto"
 *              | "open_house" | "brochure" | "volantino_stima"
 *              | "story_stima" | "story_nuovo" | "story_venduto"
 *   layout:    "elegante" | "luxury" | "moderno" | "minimal"
 *   color:     chiave di COLORS (verde_brera default)
 *   property:  { title, city, surface_area, rooms, price, photos[], … }
 *   agency:    { name, web, agent_name, agent_phone, agent_email, … }
 *   showWatermark: bool (true per anteprime free / landing demo)
 *   side:      "front" | "back" (solo volantino_stima — chip animato landing)
 */
export default function StaticTemplatePreview({
  template,
  layout = "elegante",
  color = "verde_brera",
  property,
  agency,
  showWatermark = false,
  side,
  customTexts = null,
  // iter v27 r12 — toggle "Mostra prezzo" nella brochure (cover).
  // default true (retrocompatibilità). Frontend modale propaga `showPrice`
  // → buildData passa a `data.showPrice` → ogni Cover legge `data.showPrice`
  // e nasconde il blocco prezzo se false.
  showPrice = true,
}) {
  const data = buildData(property, agency);
  data.showPrice = showPrice;
  // iter v27 r8 — l'utente può sovrascrivere ogni stringa del volantino.
  // Passiamo i testi custom (o null) come `data.texts` così il template
  // applica `customTexts[key] || DEFAULT_TEXTS[key]`.
  if (customTexts) data.texts = customTexts;
  const lay = LAYOUTS[layout] || LAYOUTS.elegante;
  const col = COLORS[color] || COLORS.verde_brera;
  const ctx = { data, lay, col, watermark: showWatermark };

  switch (template) {
    case "nuovo_annuncio":    return <NuovoAnnuncio {...ctx} />;
    case "prezzo_aggiornato": return <PrezzoAggiornato {...ctx} />;
    case "venduto":           return <Venduto {...ctx} />;
    case "open_house":        return <OpenHouse {...ctx} />;
    case "brochure":          return <Brochure {...ctx} />;
    case "brochure_social":   return <BrochureSocial {...ctx} />;
    case "volantino_stima":   return <VolantinoStima {...ctx} side={side} />;
    case "story_stima":       return <StoryStima {...ctx} />;
    case "story_nuovo":       return <StoryNuovo {...ctx} />;
    case "story_venduto":     return <StoryVenduto {...ctx} />;
    default:                  return null;
  }
}

// ─── Re-exports per backwards compatibility ─────────────────────────
// I consumer importano direttamente da "@/components/StaticTemplatePreview"
// per evitare cambi a catena.
export { LAYOUTS, COLORS } from "./staticTemplate/tokens";
export {
  BrochureCover,
  BrochureDescriptionPage,
  BrochureGalleryPage,
  BrochureContactsPage,
} from "./staticTemplate/Brochure";
